import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { useTranslations } from 'utils/translations/useTranslations';

export type Principle = {
  key: string;
  predicate: (password: string) => RegExpMatchArray | null;
};
type ValidatedPrinciple = Principle & {
  isValid: boolean;
};
type PrinciplesListProps = {
  className?: string;
  password: string;
  principles: Principle[];
  onChange?: (args: { result: boolean }) => void;
};

const PrinciplesList = ({
  className,
  password = '',
  principles: initialPrinciples = [],
  onChange,
}: PrinciplesListProps) => {
  const [principles, setPrinciples] = useState<ValidatedPrinciple[]>([]);
  const principlesValid = principles.every(principle => principle.isValid);
  const i18n = useTranslations();

  useEffect(() => {
    setPrinciples(
      initialPrinciples.map(principle => ({
        ...principle,
        isValid: !!principle.predicate(password),
      })),
    );
  }, [initialPrinciples, password]);

  useEffect(() => {
    onChange?.({ result: principlesValid });
  }, [principlesValid, onChange]);

  const principleProps = (principle: ValidatedPrinciple) => {
    return {
      'aria-checked': principle.isValid,
      'data-testid': principle.key,
      ...(password && !principle.isValid && { className: 'text-danger' }),
      ...(password && principle.isValid && { className: 'text-success' }),
      role: 'radio',
      tabIndex: -1,
    };
  };

  return (
    <ul
      aria-labelledby="password"
      className={classNames('PrinciplesList', className)}
      data-testid="password-hints"
      id="password-hints"
    >
      {principles.map(principle => (
        <li key={principle.key} {...principleProps(principle)}>
          {i18n.t(principle.key)}
        </li>
      ))}
    </ul>
  );
};

export { PrinciplesList };
